import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import ApiKeysTab from '../components/ApiKeysTab';

function AdminDashboard() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [analytics, setAnalytics] = useState(null);
  const [users, setUsers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    // Check if user is admin
    checkAdminStatus();
    loadAnalytics();
  }, []);

  useEffect(() => {
    if (activeTab === 'users') {
      loadUsers();
    } else if (activeTab === 'logs') {
      loadLogs();
    }
  }, [activeTab]);

  const checkAdminStatus = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/analytics`);
      // If we can access admin endpoint, user is admin
    } catch (error) {
      if (error.response?.status === 403) {
        navigate('/dashboard');
      }
    }
  };

  const loadAnalytics = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/analytics`);
      setAnalytics(response.data.data);
      setLoading(false);
    } catch (error) {
      console.error('Analytics error:', error);
      setLoading(false);
    }
  };

  const loadUsers = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/users?limit=100`);
      setUsers(response.data.data);
    } catch (error) {
      console.error('Users error:', error);
    }
  };

  const loadLogs = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/logs?limit=50`);
      setLogs(response.data.data);
    } catch (error) {
      console.error('Logs error:', error);
    }
  };

  const handleUserAction = async (userId, action, planId = null) => {
    try {
      await axios.put(
        `${process.env.REACT_APP_API_URL}/api/admin/users/${userId}/subscription`,
        { action, planId }
      );
      alert('User subscription updated');
      loadUsers();
      loadAnalytics();
    } catch (error) {
      alert('Error: ' + (error.response?.data?.error || 'Failed to update'));
    }
  };

  const filteredUsers = users.filter(user =>
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="container" style={{ textAlign: 'center', padding: '100px 20px' }}>
        <h2>Loading Admin Dashboard...</h2>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '16px 24px'
      }}>
        <div className="container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '24px', marginBottom: '4px' }}>🔧 Super Admin Dashboard</h1>
            <p style={{ color: '#666', fontSize: '14px' }}>Welcome, {user?.name || 'Admin'}</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button onClick={() => navigate('/dashboard')} style={{ background: '#6c757d' }}>
              User Dashboard
            </button>
            <button onClick={logout} style={{ background: '#dc3545' }}>
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div style={{
        background: 'white',
        borderBottom: '1px solid #e0e0e0',
        padding: '0 24px'
      }}>
        <div className="container" style={{ display: 'flex', gap: '32px' }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'users', label: 'Users' },
            { key: 'api-keys', label: 'API Keys' },
            { key: 'logs', label: 'Logs' }
          ].map(tab => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                background: 'none',
                border: 'none',
                padding: '16px 0',
                borderBottom: activeTab === tab.key ? '3px solid #007bff' : '3px solid transparent',
                color: activeTab === tab.key ? '#007bff' : '#666',
                cursor: 'pointer',
                fontWeight: activeTab === tab.key ? 'bold' : 'normal'
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="container" style={{ padding: '32px 24px' }}>
        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <div>
            {/* Stats Cards */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '32px'
            }}>
              <div className="card" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                <h3 style={{ fontSize: '16px', opacity: 0.9, marginBottom: '8px' }}>Total Users</h3>
                <p style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>{analytics.totalUsers}</p>
                <p style={{ fontSize: '14px', opacity: 0.8 }}>All registered users</p>
              </div>

              <div className="card" style={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                <h3 style={{ fontSize: '16px', opacity: 0.9, marginBottom: '8px' }}>Active Subscriptions</h3>
                <p style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>{analytics.activeSubscriptions}</p>
                <p style={{ fontSize: '14px', opacity: 0.8 }}>Paying customers</p>
              </div>

              <div className="card" style={{ background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', color: 'white' }}>
                <h3 style={{ fontSize: '16px', opacity: 0.9, marginBottom: '8px' }}>Monthly Revenue</h3>
                <p style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>${analytics.mrr.toLocaleString()}</p>
                <p style={{ fontSize: '14px', opacity: 0.8 }}>MRR</p>
              </div>

              <div className="card" style={{ background: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', color: 'white' }}>
                <h3 style={{ fontSize: '16px', opacity: 0.9, marginBottom: '8px' }}>Total Raffles</h3>
                <p style={{ fontSize: '36px', fontWeight: 'bold', marginBottom: '8px' }}>{analytics.totalRaffles}</p>
                <p style={{ fontSize: '14px', opacity: 0.8 }}>{analytics.totalEntries} entries</p>
              </div>
            </div>

            {/* Plan Distribution */}
            <div className="card">
              <h3 style={{ marginBottom: '20px' }}>Plan Distribution</h3>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                {analytics.planDistribution.map(plan => (
                  <div key={plan.plan_id} style={{
                    padding: '16px',
                    background: '#f8f9fa',
                    borderRadius: '8px',
                    borderLeft: '4px solid #007bff'
                  }}>
                    <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '4px' }}>{plan.count}</div>
                    <div style={{ color: '#666' }}>{plan.plan_id}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Recent Signups Chart */}
            <div className="card" style={{ marginTop: '20px' }}>
              <h3 style={{ marginBottom: '20px' }}>Recent Signups (Last 30 Days)</h3>
              <div style={{ height: '200px', display: 'flex', alignItems: 'flex-end', gap: '8px' }}>
                {analytics.recentSignups.slice(0, 15).map((day, index) => {
                  const maxCount = Math.max(...analytics.recentSignups.map(d => parseInt(d.count)));
                  const height = (parseInt(day.count) / maxCount) * 100;
                  return (
                    <div key={index} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div style={{
                        width: '100%',
                        height: `${height}%`,
                        background: '#007bff',
                        borderRadius: '4px 4px 0 0',
                        minHeight: height > 0 ? '20px' : '0'
                      }} />
                      <div style={{ fontSize: '10px', color: '#666', marginTop: '4px' }}>
                        {new Date(day.date).getDate()}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* Users Tab */}
        {activeTab === 'users' && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <input
                type="text"
                placeholder="Search users by email or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ maxWidth: '400px' }}
              />
            </div>

            <div className="card">
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>User</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Plan</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Status</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Location</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map(user => (
                    <tr key={user.id} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>
                        <div style={{ fontWeight: '500' }}>{user.name || 'N/A'}</div>
                        <div style={{ fontSize: '14px', color: '#666' }}>{user.email}</div>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          background: user.plan_id === 'AGENCY' ? '#007bff' : user.plan_id === 'PROFESSIONAL' ? '#28a745' : '#6c757d',
                          color: 'white',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {user.plan_id || 'FREE'}
                        </span>
                      </td>
                      <td style={{ padding: '12px' }}>
                        <span style={{
                          padding: '4px 12px',
                          background: user.subscription_status === 'active' ? '#d4edda' : '#f8d7da',
                          color: user.subscription_status === 'active' ? '#155724' : '#721c24',
                          borderRadius: '12px',
                          fontSize: '12px'
                        }}>
                          {user.subscription_status || 'none'}
                        </span>
                      </td>
                      <td style={{ padding: '12px', fontSize: '14px', color: '#666' }}>
                        {user.ghl_location_id ? user.ghl_location_id.substring(0, 8) + '...' : 'N/A'}
                      </td>
                      <td style={{ padding: '12px' }}>
                        <button
                          onClick={() => setSelectedUser(user)}
                          style={{ padding: '6px 12px', fontSize: '14px' }}
                        >
                          Manage
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'api-keys' && (
          <ApiKeysTab />
        )}

        {/* Logs Tab */}
        {activeTab === 'logs' && (
          <div className="card">
            <h3 style={{ marginBottom: '20px' }}>Admin Action Logs</h3>
            <div>
              {logs.map(log => (
                <div key={log.id} style={{
                  padding: '16px',
                  borderBottom: '1px solid #f0f0f0',
                  display: 'flex',
                  justifyContent: 'space-between'
                }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                      {log.action_type.replace('_', ' ').toUpperCase()}
                    </div>
                    <div style={{ fontSize: '14px', color: '#666' }}>
                      {log.description}
                    </div>
                    <div style={{ fontSize: '12px', color: '#999', marginTop: '4px' }}>
                      By: {log.admin_email} → Target: {log.target_email || 'N/A'}
                    </div>
                  </div>
                  <div style={{ fontSize: '12px', color: '#999' }}>
                    {new Date(log.created_at).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* User Management Modal */}
      {selectedUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div className="card" style={{ maxWidth: '500px', width: '90%' }}>
            <h3 style={{ marginBottom: '20px' }}>Manage User: {selectedUser.email}</h3>

            <div style={{ marginBottom: '20px' }}>
              <p><strong>Current Plan:</strong> {selectedUser.plan_id || 'FREE'}</p>
              <p><strong>Status:</strong> {selectedUser.subscription_status || 'none'}</p>
            </div>

            <h4 style={{ marginBottom: '12px' }}>Change Plan:</h4>
            <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '20px' }}>
              {['FREE', 'STARTER', 'PROFESSIONAL', 'AGENCY'].map(plan => (
                <button
                  key={plan}
                  onClick={() => handleUserAction(selectedUser.id, 'change', plan)}
                  style={{ padding: '8px 16px', fontSize: '14px' }}
                >
                  {plan}
                </button>
              ))}
            </div>

            <h4 style={{ marginBottom: '12px' }}>Actions:</h4>
            <div style={{ display: 'flex', gap: '8px', marginBottom: '20px' }}>
              <button
                onClick={() => handleUserAction(selectedUser.id, 'cancel')}
                style={{ background: '#dc3545', flex: 1 }}
              >
                Cancel Subscription
              </button>
            </div>

            <button
              onClick={() => setSelectedUser(null)}
              style={{ background: '#6c757d', width: '100%' }}
            >
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AdminDashboard;
